import { bookActions } from '@/actions/bookActions';
import {
  getNewExtraTimeAddOns,
  getRecommendedServices,
} from '@/components/modules/modals/BookAppointment/BookAppointment.helpers';
import { ROUTE_BUILDER } from '@/constants/pages';
import { ANY_EMPLOYEE_ID, getAnyEmployee } from '@/features/booking-selection/constants';
import { isServer } from '@/helpers/general';
import { getExtraTimeRequirementForUser } from '@/helpers/userHelper';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { BookState, EXTRA_TIME_REQUIREMENT } from '@/types/state/book';
import { Place, PlaceService } from '@/types/state/shared';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useBookingFlowChrome } from './BookingFlowChromeContext';
import { BOOKING_FLOW_STEP_SLUGS, BookingFlowStep, PLACE_READY_TIMEOUT_MS } from './constants';
import { getStepFromPathname } from './goToStep';
import { resolveEmployeeFromUrl } from './resolveEmployeeFromUrl';
import { resolveMainServicesFromUrl } from './resolveMainServicesFromUrl';
import { selectionPatchFromBookState } from './selectionPatchFromBookState';
import { syncTrackedServiceIds } from './tracking';
import { BookingFlowSelectionPatch, parseSelection, serialiseSelection } from './urlState';

/**
 * The param each step page reads back as its own answer. A repair must never write it: the
 * step's guard renders the page only while its question is unanswered, so an answer written
 * into the entry bounces the visitor forward again and back looks dead. The optional steps are
 * absent on purpose — `/tillval` may remain with `rs` set (its remain rule counts main
 * services), and `et` is no part of a repair to begin with.
 */
const STEP_ANSWER_PARAM: Partial<Record<BookingFlowStep, keyof BookingFlowSelectionPatch>> = {
  [BOOKING_FLOW_STEP_SLUGS.SELECT_SERVICE]: 'ss',
  [BOOKING_FLOW_STEP_SLUGS.SELECT_EMPLOYEE]: 'sr',
};

/**
 * Histories that have navigated at least once inside this document. Until one has, a `POP` is
 * not a back navigation: the first load and a reload report `POP` too, and there `state.book`
 * is `bookReducer`'s sessionStorage seed rather than an edit the visitor just made — repairing
 * from it would let a stale seed rewrite a deep link.
 */
const navigatedHistories = new WeakSet<object>();

type BookingFlowRouteParams = { placeSlugId: string };

/** Shape `bookActions.addPlace`/`bookAgain` persist into sessionStorage's `bookPlace` key. */
type CachedBookPlace = { id?: number; slug?: string };

function readCachedPlaceId(): number | undefined {
  if (isServer) return undefined;
  try {
    const raw = sessionStorage.getItem('bookPlace');
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as CachedBookPlace;
    return typeof parsed?.id === 'number' ? parsed.id : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Derives `state.book` from the booking-flow query string (R2, R9, R12). Keyed on
 * `[place?.id, location.search]` so it also runs when the place arrives on a later render
 * (cold load) and on back/forward (the router updates `location.search`; no popstate listener).
 *
 * Hydration replaces, never merges (R2): every run pops the current selection before adding
 * back exactly what the URL names, so `bookReducer`'s unscoped sessionStorage seed cannot
 * survive underneath a deep link. The one exception is a pop that *crosses screens* — one that
 * mounts this page, rather than one that finds it already mounted. Every screen writes its
 * selection into its own entry only, so a pop out of the calendar (or out of a later step)
 * lands on an entry that never saw that screen's edits: `ss`/`rs`/`sr` there are known stale.
 * Such a run repairs the entry from the store (replace) and lets the re-run hydrate URL-first
 * from it. A pop that leaves this page mounted is a same-step push being undone — add-on picks
 * on `/tillval`, the employee answer — and stays URL-first, which is what makes back an undo
 * (KTD15). A cached selection for a *different* place (R12) is
 * discarded with `clearBook()` up front — it must stay `clearBook`, not per-service pops:
 * `popService` assumes `state.services` is an array, which the SSR-merge seed does not
 * guarantee.
 *
 * If no place shows up within `PLACE_READY_TIMEOUT_MS`, redirects (replace) to the place page
 * — or to the chrome context's `exitTo` when one is provided (the widget has no place page) —
 * rather than rendering an empty shell forever.
 */
export function useBookingFlowSelection(place?: Place): void {
  const { placeSlugId } = useParams<BookingFlowRouteParams>();
  const location = useLocation();
  const history = useHistory();
  const { exitTo } = useBookingFlowChrome();
  const dispatch = useAppDispatch();
  const bookstate = useAppSelector((state) => state.book) as BookState;
  const { user } = useAppSelector((state) => state.users);
  const [placeWaitTimedOut, setPlaceWaitTimedOut] = useState(false);
  // Mount-scoped, and the whole of the cross-screen test: a step page that a pop leaves mounted
  // can only have been popped within its own route.
  const isFirstRunRef = useRef(true);

  const hasPlace = Boolean(place) || Boolean(bookstate.place);

  useEffect(() => {
    if (hasPlace) return undefined;
    const timer = setTimeout(() => setPlaceWaitTimedOut(true), PLACE_READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [hasPlace]);

  useEffect(() => {
    if (hasPlace || !placeWaitTimedOut) return;
    // Query preserved like every other flow exit — `sistaminuten`/`dealId`/`sb` must survive.
    history.replace({ pathname: exitTo ?? ROUTE_BUILDER.placePage(placeSlugId), search: location.search });
  }, [hasPlace, placeWaitTimedOut, placeSlugId, history, location.search, exitTo]);

  // Subscribed for as long as any step page is mounted, which is long enough: the navigation
  // that leaves a step entry is a push or replace made from that very page.
  useEffect(
    () =>
      history.listen((_, action) => {
        if (action !== 'POP') navigatedHistories.add(history);
      }),
    [history],
  );

  useEffect(() => {
    if (!place) return;

    const isFirstRun = isFirstRunRef.current;
    isFirstRunRef.current = false;

    const isCrossScreenPop = isFirstRun && history.action === 'POP' && navigatedHistories.has(history);
    if (isCrossScreenPop && bookstate.place?.id === place.id) {
      const patch = selectionPatchFromBookState(bookstate, place);
      const step = getStepFromPathname(location.pathname);
      const ownAnswer = step ? STEP_ANSWER_PARAM[step] : undefined;
      if (ownAnswer) delete patch[ownAnswer];
      const patched = serialiseSelection(location.search, patch);
      // `rs` is relative to `ss`, so an entry naming add-ons but no main service is one the two
      // readers of the URL disagree about: hydration resolves `rs` against the picked mains and
      // finds nothing, while `deriveStepSelection` looks the ids up on their own and reports a
      // service picked. No screen can put the visitor in that state either — where the repair
      // leaves `ss` unwritten, `rs` goes with it.
      const repaired = parseSelection(patched).ss ? patched : serialiseSelection(patched, { rs: null });
      if (repaired !== location.search) {
        history.replace({ pathname: location.pathname, search: repaired, state: location.state });
        return;
      }
    }

    const cachedPlaceId = readCachedPlaceId();
    const isCrossPlaceCache = cachedPlaceId !== undefined && cachedPlaceId !== place.id;
    if (isCrossPlaceCache) {
      dispatch(bookActions.clearBook());
    }

    const urlSelection = parseSelection(location.search);
    const pickedMainServices = resolveMainServicesFromUrl(urlSelection.ss, place);

    const { matched: pickedEmployee, isAnyEmployee } = resolveEmployeeFromUrl(urlSelection.sr, place.employees);

    if (pickedEmployee) {
      dispatch(bookActions.pickEmployee(pickedEmployee.id, pickedEmployee.about?.priceListId));
      dispatch(bookActions.keepEmployee());
      dispatch(bookActions.addPlace(place));
    } else if (isAnyEmployee) {
      // sr=0 is the any-employee sentinel, not an externalId — without this branch the
      // else-arm would removeEmployee and the valj-utovare guard would bounce forever.
      dispatch(bookActions.pickEmployee(ANY_EMPLOYEE_ID, undefined));
      dispatch(bookActions.keepEmployee());
      dispatch(bookActions.addPlace(place));
    } else {
      dispatch(bookActions.removeEmployee());
      dispatch(bookActions.addPlace(place));
    }

    const existingServices = isCrossPlaceCache ? [] : bookstate.services ?? [];
    existingServices.forEach((service) => {
      dispatch(
        // @ts-ignore popService is a thunk; AppDispatch isn't typed thunk-aware (see BookAppointment.handlers.ts)
        bookActions.popService(
          service.id,
          bookstate.time?.timestamp,
          existingServices,
          bookstate.employee,
          place,
          true,
        ),
      );
    });

    pickedMainServices.forEach((service) => {
      // @ts-ignore addService is a thunk; AppDispatch isn't typed thunk-aware (see BookAppointment.handlers.ts)
      dispatch(bookActions.addService(service, place));
    });

    const pickedAddOns: PlaceService[] = [];
    if (urlSelection.rs) {
      const addOnIds = urlSelection.rs.split(',').map((id) => parseInt(id, 10));
      const recommended = getRecommendedServices(pickedMainServices, pickedEmployee ?? getAnyEmployee());
      pickedAddOns.push(...recommended.filter((service) => service.id != null && addOnIds.includes(service.id)));
      pickedAddOns.forEach((service) => {
        dispatch(bookActions.appendExtraService(service, pickedMainServices));
      });
    }

    if (urlSelection.et === '1') {
      // @ts-ignore setExtraTimeRequirement is a thunk; AppDispatch isn't typed thunk-aware (see BookAppointment.handlers.ts)
      dispatch(bookActions.setExtraTimeRequirement(EXTRA_TIME_REQUIREMENT.NeedsExtraTime, place.id));
    } else if (urlSelection.et === '0') {
      // @ts-ignore setExtraTimeRequirement is a thunk; AppDispatch isn't typed thunk-aware (see BookAppointment.handlers.ts)
      dispatch(bookActions.setExtraTimeRequirement(EXTRA_TIME_REQUIREMENT.NoExtraTime, place.id));
    }

    // A "needs extra time" answer must also materialise the extra-time add-ons — they change
    // the price/duration the calendar books. Precedence: URL answer, then the tab's own
    // answer for this flow (set on the question page, restored from sessionStorage on
    // reload, cleared by clearBook on every fresh entry), then the stored profile answer.
    // The session answer must beat the profile: a rewind revision would otherwise be undone
    // here on the next et-less step entry, re-adding extra time the user just declined —
    // the profile answer stays write-once until persistence moves to booking completion.
    // Cross-place cache: the lingering answer belongs to another place (the clearBook above
    // does not reach this run's closure) — ignore it.
    const sessionExtraTimeRequirement = isCrossPlaceCache ? undefined : bookstate.extraTimeRequirement;
    const effectiveExtraTimeRequirement =
      urlSelection.et === '1'
        ? EXTRA_TIME_REQUIREMENT.NeedsExtraTime
        : urlSelection.et === '0'
        ? EXTRA_TIME_REQUIREMENT.NoExtraTime
        : sessionExtraTimeRequirement || getExtraTimeRequirementForUser(user, place.id);

    const hydratedServices = [...pickedMainServices, ...pickedAddOns];
    const extraTimeAddOns =
      effectiveExtraTimeRequirement === EXTRA_TIME_REQUIREMENT.NeedsExtraTime
        ? getNewExtraTimeAddOns(hydratedServices, pickedEmployee ?? getAnyEmployee())
        : [];
    extraTimeAddOns.forEach((addOn) => {
      dispatch(bookActions.appendExtraService(addOn, hydratedServices));
    });

    // The URL is what changed the selection here, not a click handler, so the sticky
    // service_ids must follow what was just materialised (U15).
    syncTrackedServiceIds([...hydratedServices, ...extraTimeAddOns]);
    // Deliberately not depending on `bookstate` or `user`: this must run only when the place
    // identity or the URL changes, never because a previous run's dispatches updated the store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place?.id, location.search]);
}

export default useBookingFlowSelection;
